/*
 * RiderTracker — mobile-first "Where's my ride?" page.
 * Rendered at /r/:token. No auth (token is auth).
 * Polls /api/tpt/rider/{token} every 8s for status + driver GPS + ETA.
 * Uses TPGoogleMap in a compact fit for driver → pickup → dropoff.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Car, CheckCircle2, XCircle, Loader2, BellRing } from "lucide-react";
import TPGoogleMap from "../components/TPGoogleMap";
import SMSComplianceNotice from "../components/SMSComplianceNotice";
import CallSupportButton from "../components/CallSupportButton";
import usePushNotifications from "../hooks/usePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tpt`;

const STATUS_COPY = {
  ACCEPTED: { label: "Trip received", tone: "slate", desc: "Your ride is being prepared." },
  OFFERED_TP: { label: "Finding a driver", tone: "amber", desc: "We're routing a driver to your ride." },
  ASSIGNED: { label: "Driver assigned", tone: "blue", desc: "Your driver has the trip — they'll head your way shortly." },
  EN_ROUTE: { label: "Driver is on the way", tone: "green", desc: "Your driver is heading to pickup." },
  ARRIVED_PU: { label: "Driver has arrived", tone: "violet", desc: "Your driver is at the pickup location." },
  ON_TRIP: { label: "On the way to your destination", tone: "blue", desc: "You are on the trip." },
  ARRIVED_DO: { label: "Arrived at destination", tone: "violet", desc: "Please review and sign for the trip." },
  COMPLETED: { label: "Trip completed", tone: "green", desc: "Thanks for riding with MedexOVA!" },
  CANCELLED: { label: "Trip cancelled", tone: "red", desc: "This trip has been cancelled." },
  NO_SHOW_CONFIRMED: { label: "Marked as no-show", tone: "red", desc: "The driver was unable to reach you." },
  REASSIGN_NEEDED: { label: "Finding another driver", tone: "amber", desc: "Your driver became unavailable — we're finding another." },
};

const TONE_STYLES = {
  slate: "bg-slate-100 text-slate-700",
  amber: "bg-amber-100 text-amber-800",
  blue: "bg-blue-100 text-blue-800",
  green: "bg-green-100 text-green-800",
  violet: "bg-violet-100 text-violet-800",
  red: "bg-red-100 text-red-800",
};

export default function RiderTracker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const push = usePushNotifications({ riderToken: token });

  const enablePush = async () => {
    const ok = await push.enable();
    if (ok) toast.success("Live updates enabled");
    else if (push.error) toast.error(push.error);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/rider/${token}`);
      setData(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load trip");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  if (loading) return <FullscreenLoader />;
  if (error) return <ErrorState msg={error}/>;

  const st = STATUS_COPY[data.status] || { label: data.status, tone: "slate", desc: "" };
  const isTerminal = ["COMPLETED", "CANCELLED", "NO_SHOW_CONFIRMED"].includes(data.status);
  const showMap = data.pickup?.lat && data.dropoff?.lat;
  const brand = data.branding;

  return (
    <div className="min-h-screen bg-gradient-to-b from-bone via-white to-slate-50 pb-20">
      <header className="px-4 py-4 flex items-center justify-between border-b border-[#E9EAE4] bg-white sticky top-0 z-10">
        <div className="flex items-center gap-2">
          {brand?.logo_url ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt={brand.tp_name} className="h-8 w-8 rounded-md object-contain" data-testid="rider-tp-logo" />
          ) : (
            <div className="h-8 w-8 rounded-md bg-forest text-white grid place-items-center font-bold text-sm">A</div>
          )}
          <div>
            <div className="font-bold text-slate_navy text-sm leading-none" style={brand?.primary_color ? { color: brand.primary_color } : undefined}>
              {brand?.tp_name || "MedexOVA Transit"}
            </div>
            <div className="text-[10px] text-slate-500">Rider tracking</div>
          </div>
        </div>
        <div className="text-[10px] text-slate-400 font-mono">#{data.trip_id.slice(0, 8)}</div>
      </header>

      <main className="max-w-md mx-auto px-4 pt-6 space-y-6">
        <div>
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3 ${TONE_STYLES[st.tone]}`}>
            {isTerminal ? (data.status === "COMPLETED" ? <CheckCircle2 size={14}/> : <XCircle size={14}/>) : <Loader2 size={14} className="animate-spin"/>}
            {st.label}
          </div>
          <h1 className="text-2xl font-bold text-slate_navy leading-tight mb-1">Hi {data.rider_name || "there"} 👋</h1>
          <p className="text-sm text-slate-600 leading-relaxed">{st.desc}</p>
          {data.eta_min != null && data.status === "EN_ROUTE" && (
            <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-forest">
              <Clock size={14}/> Estimated arrival: {data.eta_min} min
            </div>
          )}
        </div>

        {!isTerminal && <CallSupportButton riderToken={token} />}

        {showMap && (
          <div className="rounded-2xl overflow-hidden">
            <TPGoogleMap
              pickup={data.pickup}
              dropoff={data.dropoff}
              drivers={data.driver_gps?.lat ? [{
                driver_id: "d",
                name: data.assigned_driver?.name || "Driver",
                last_seen_lat: data.driver_gps.lat,
                last_seen_lng: data.driver_gps.lng,
                shift_status: "on_shift",
              }] : []}
              height={280}
              emptyLabel="Waiting for driver location…"
            />
          </div>
        )}

        {data.assigned_driver && (
          <div className="bg-white border border-[#E2E0D8] rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Your driver</div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-forest text-white grid place-items-center font-bold text-lg">
                {(data.assigned_driver.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="font-bold text-slate_navy">{data.assigned_driver.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Car size={11}/> {data.assigned_driver.vehicle_type || "sedan"}
                </div>
              </div>
              {data.assigned_driver.phone_masked && (
                <a href="tel:+18336592699"
                   className="p-2 rounded-full bg-forest text-white"
                   aria-label="Call dispatch"
                   data-testid="rider-call-dispatch">
                  <Phone size={16}/>
                </a>
              )}
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E2E0D8] rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Trip route</div>
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-green-600 mt-0.5 shrink-0"/>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Pickup</div>
                <div className="text-sm text-slate_navy">{data.pickup.address}</div>
              </div>
            </div>
            <div className="border-l-2 border-dashed border-slate-300 ml-2 h-4"/>
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-red-600 mt-0.5 shrink-0"/>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Drop-off</div>
                <div className="text-sm text-slate_navy">{data.dropoff.address}</div>
              </div>
            </div>
          </div>
        </div>

        <SMSComplianceNotice compact />

        {push.supported && push.permission !== "granted" && (
          <button
            onClick={enablePush}
            disabled={push.enabling}
            className="w-full flex items-center justify-center gap-2 bg-white border border-forest text-forest rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
            data-testid="rider-enable-push-btn"
          >
            <BellRing size={16}/> {push.enabling ? "Enabling…" : "Yes, text/push me live updates"}
          </button>
        )}
        {push.permission === "granted" && (
          <div className="text-center text-xs text-green-700 font-semibold" data-testid="rider-push-enabled-note">
            Live updates enabled ✓
          </div>
        )}

        <div className="text-center text-xs text-slate-500 pt-4">
          Need help? Call dispatch at{" "}
          <a href="tel:+18336592699" className="text-forest font-semibold underline">1-833-659-2699</a>
        </div>
      </main>
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div className="min-h-screen grid place-items-center text-slate-500">
      <Loader2 className="w-8 h-8 animate-spin"/>
    </div>
  );
}

function ErrorState({ msg }) {
  return (
    <div className="min-h-screen grid place-items-center px-4 text-center">
      <div>
        <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3"/>
        <div className="font-bold text-slate_navy mb-1">We couldn&apos;t load your trip</div>
        <div className="text-sm text-slate-500 mb-4">{msg}</div>
        <a href="tel:+18336592699" className="inline-block bg-forest text-white px-4 py-2 rounded-md text-sm font-semibold">
          Call dispatch: 1-833-659-2699
        </a>
      </div>
    </div>
  );
}
